/**
 * KANBAN CREATE — creazione di una bacheca: nome + pipeline (che definisce le colonne).
 *
 * TS-101: nessuna selezione di preventivi — la board mostra a runtime i preventivi che soddisfano
 * il suo filtro. Il filtro (TS-102) si puo' lasciare vuoto — allora la board mostra tutti i
 * preventivi della sua pipeline — e comunque si modifica dopo, dalla board stessa.
 */

import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Button from '@mui/material/Button';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { useToast } from '../../components/useToast';
import { ChevronDownGlyph } from '../../components/icons';
import { fetchPipelines } from '../pipeline/pipelineThunks';
import type { PipelineSliceState } from '../pipeline/pipelineTypes';
import { createKanban } from './kanbanThunks';
import { kanbanApi } from './kanbanApi';
import { KanbanFilterEditor } from './KanbanFilterEditor';
import { useCurrentGroupId } from '../../auth/authSelectors';
import { getEntityLabelByFqn } from '../../entityRegistry/entityRegistry';
import { useIsClassNameDisabled } from '../../plugin/useCoreItems';
import type { KanbanFilter, KanbanFilterField } from './kanbanMappers';

export default function KanbanCreate() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const toast = useToast();
  const { items: allPipelines } = useAppSelector((state) => state.pipelines as PipelineSliceState);
  // Una board non si crea su una pipeline di un tipo spento (FILO-53).
  const isClassNameDisabled = useIsClassNameDisabled();
  const pipelines = useMemo(
    () => allPipelines.filter((p) => !isClassNameDisabled(p.className)),
    [allPipelines, isClassNameDisabled],
  );

  const [name, setName] = useState('');
  const [pipelineId, setPipelineId] = useState(0);
  const [filter, setFilter] = useState<KanbanFilter | null>(null);
  const [filterFields, setFilterFields] = useState<KanbanFilterField[]>([]);
  const [saving, setSaving] = useState(false);
  const groupId = useCurrentGroupId();

  useEffect(() => {
    if (allPipelines.length === 0) dispatch(fetchPipelines({ pageSize: 1000 }));
  }, [allPipelines.length, dispatch]);

  // Catalogo dei campi filtrabili. Se non arriva l'elenco resta vuoto e l'editor lo dice:
  // meglio una maschera che dichiara di non sapere di una che propone zero campi.
  //
  // Il catalogo e' DEL TIPO, e il tipo lo porta la pipeline scelta: finche' non se ne sceglie una
  // non c'e' catalogo da chiedere, e cambiandola il catalogo cambia. Chiederlo una volta sola
  // all'apertura, com'era prima, avrebbe proposto i campi del preventivo anche su un funnel di task.
  const selectedClassName = pipelines.find((p) => p.pipelineId === pipelineId)?.className ?? '';

  useEffect(() => {
    // Cambiare TIPO butta via il filtro gia' composto: e' stato costruito su un altro catalogo, e
    // tenerlo lo renderebbe invisibile (l'editor disegna solo cio' che il catalogo conosce) ma
    // ancora presente nel salvataggio — una creazione respinta con 400 per un filtro che in
    // maschera non c'e' piu'. Cambiare pipeline SENZA cambiare tipo non lo tocca: l'effetto
    // dipende dal className, non dal pipelineId.
    setFilter(null);

    if (!groupId || !selectedClassName) {
      setFilterFields([]);

      return;
    }

    let alive = true;
    kanbanApi.getFilterFields(groupId, selectedClassName)
      .then((f) => { if (alive) setFilterFields(f); })
      // Un catalogo che non arriva si dice. Vedi lo stesso catch in KanbanBoardPage: tacendo, un
      // errore dell'endpoint diventava "questo tipo non ha campi filtrabili".
      .catch((err) => {
        if (!alive) return;
        setFilterFields([]);
        toast({
          severity: 'error',
          message: t('kanban.filter.catalogFailed', {
            reason: err instanceof Error ? err.message : String(err),
          }),
        });
      });
    return () => { alive = false; };
  }, [groupId, selectedClassName, toast, t]);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    await handleCreate();
  }

  async function handleCreate() {
    if (!name.trim()) {
      toast({ severity: 'error', message: t('kanban.create.nameRequired') });
      return;
    }
    if (pipelineId <= 0) {
      toast({ severity: 'error', message: t('kanban.create.pipelineRequired') });
      return;
    }
    setSaving(true);
    try {
      const created = await dispatch(
        createKanban({ name: name.trim(), pipelineId, filter }),
      ).unwrap();
      toast({ severity: 'success', message: t('common.saved') });
      navigate(`/kanban/${created.kanbanId}`);
    } catch (err) {
      toast({ severity: 'error', message: err instanceof Error ? err.message : String(err) });
      setSaving(false);
    }
  }

  return (
    // Stessa impaginazione del form preventivo e di quello pipeline: link di uscita
    // sopra l'intestazione, i blocchi su carta, le azioni in fondo a destra. Prima
    // questa pagina era l'unica a impaginarsi da sé con le utility di spaziatura, e
    // si vedeva: campi a filo schermo, nessuna carta sotto, titolo di taglia diversa.
    <>
      <Link to="/kanban" className="tpl-back">
        <ChevronDownGlyph />
        {t('kanban.create.back')}
      </Link>
      <div className="page-head">
        <div>
          <h1 className="page-title">{t('kanban.newKanban')}</h1>
        </div>
      </div>

      <form className="qform" onSubmit={handleSubmit}>
        <section className="blk">
          <div className="blk__head">
            <h2 className="blk__title">{t('kanban.create.stepPipeline')}</h2>
          </div>
          <div className="blk__body">
            <TextField
              required
              label={t('kanban.create.name')}
              value={name}
              onChange={(e) => setName(e.target.value)}
              fullWidth
              className="form-field"
            />
            <TextField
              select
              required
              label={t('kanban.create.pipeline')}
              value={pipelineId || ''}
              onChange={(e) => setPipelineId(e.target.value === '' ? 0 : Number(e.target.value))}
              fullWidth
              className="form-field"
            >
              <MenuItem value="">{t('kanban.create.selectPipeline')}</MenuItem>
              {/* TS-117: accanto al nome c'e' il TIPO, perche' la board che nasce e' del tipo della
                  pipeline scelta. Non si filtra l'elenco: ogni pipeline e' una scelta legittima, e il
                  vincolo che il ticket immaginava ("solo le pipeline compatibili") non esiste — quello
                  che serviva era rendere visibile che bacheca si sta creando. */}
              {pipelines.map((p) => (
                <MenuItem key={p.pipelineId} value={p.pipelineId}>
                  {p.name} — {getEntityLabelByFqn(p.className, t)}
                </MenuItem>
              ))}
            </TextField>
          </div>
        </section>

        {/* Il filtro sta DOPO la pipeline perché la restringe: prima si sceglie l'insieme,
            poi che fetta guardarne. Facoltativo — ma è anche ciò che POPOLA la bacheca, e senza
            resta vuota finché qualcuno non colloca i record uno per uno dalla loro scheda. Lo si
            dice qui invece di lasciarlo scoprire a bacheca creata.

            Non è dentro un `.blk`: l'editor porta già la propria carta con intestazione e
            spiegazione, e incartarlo due volte darebbe due titoli per la stessa cosa. */}
        <KanbanFilterEditor
          filter={filter}
          fields={filterFields}
          onChange={setFilter}
          disabled={saving}
        />

        {!filter?.groups?.length && (
          <p className="text-xs text-muted mt-sm">{t('kanban.populate.noFilter')}</p>
        )}

        {/* Azioni a destra: prima l'uscita, poi l'azione conclusiva. */}
        <div className="qform__bar">
          <Button type="button" variant="outlined" onClick={() => navigate('/kanban')} disabled={saving}>
            {t('common.cancel')}
          </Button>
          <Button type="submit" variant="contained" disabled={saving}>
            {saving ? t('common.loading') : t('kanban.create.create')}
          </Button>
        </div>
      </form>
    </>
  );
}
